// src/theme/legacy.js
//
// Helpers for replacing hard-coded colours without moving plum/gold by a pixel (spec
// 20260930-caro-editorial-theme, T053, LD-9, AC-13).
//
// Every call site passes the OLD literal as the fallback. Themes without an entry — plum, gold and
// the presets — therefore render exactly what they rendered before; a theme that defines the key
// (Caro Editorial, `legacy` in its plugin) gets its own token instead. Because the old value lives
// at the call site, it cannot drift from what the page showed on main.
//
// Status helpers follow the same rule: only a theme with `statusChips` (Editorial) swaps a
// hand-picked green/red/amber pair for its status pair; everywhere else the old pair stays.

/** Every legacy key a theme may define. Editorial defines all of them (legacy.test.js). */
export const LEGACY_KEYS = [
  // shell (phase 4)
  'presence',
  'settingsPanelBg',
  'settingsRowHoverBg',
  'settingsEmailActiveBg',
  'settingsPushActiveBg',
  'pageHeaderTitle',
  // pages/Health.js (T054) — a standalone status page with its own dark slate look today
  'health.pageBg',
  'health.panelBg',
  'health.panelBgSubtle',
  'health.panelBorder',
  'health.panelBorderHover',
  'health.accent',
  'health.text',
  'health.textMuted',
  'health.textSubtle',
  // pages/EinkDesigner/index.jsx (T061) — editor chrome only, never the e-ink canvas
  'eink.accent',
  'eink.accentSoft',
  'eink.border',
];

/** `theme.caro.legacy[key]`, or the old literal. */
export const legacy = (theme, key, fallback) => theme?.caro?.legacy?.[key] ?? fallback;

/** Same, as an MUI `sx` value callback: `color: legacyColor('health.text', '#f1f5f9')`. */
export const legacyColor = (key, fallback) => (theme) => legacy(theme, key, fallback);

/**
 * A hand-picked `{ bg, color }` pair that means a status. Editorial: its status pair
 * (`{ bg, color, border }`); every other theme: the old pair object, unchanged.
 */
export const statusPair = (theme, kind, fallbackPair) => {
  if (!theme?.caro?.statusChips) return fallbackPair;
  const s = theme.caro.status[kind] || theme.caro.status.neutral;
  return { bg: s.bg, color: s.fg, border: s.border };
};

/**
 * `sx` callback for an element coloured with a hand-picked `{ bg, color }` status pair:
 * `sx={pairSx('available', { bg: '#E8F5E9', color: '#2E7D32' }, { fontWeight: 600 })}`.
 * Old themes get exactly `{ bgcolor, color, ...extra }` — the same object the page used before.
 */
export const pairSx = (kind, fallbackPair, extra = {}) => (theme) => {
  const p = statusPair(theme, kind, fallbackPair);
  return {
    bgcolor: p.bg,
    color: p.color,
    ...extra,
    ...(p.border ? { border: '1px solid', borderColor: p.border } : {}),
  };
};

/**
 * The Material shades the top-10 pages hand-picked for statuses, with the status they mean.
 * Used by hexFg / hexPairSx so page data maps (call statuses, SIP events, …) keep their literal
 * and only gain a meaning. Unknown hex values are never remapped.
 */
export const HEX_STATUS = {
  // greens — OK / answered / active
  '#2E7D32': 'available', '#E8F5E9': 'available', '#10B981': 'available',
  // reds — missed / failed / expired
  '#C62828': 'urgent', '#FFEBEE': 'urgent', '#EF4444': 'urgent',
  // ambers — voicemail / unreachable / expiring
  '#F57F17': 'rollout', '#FFF8E1': 'rollout', '#E65100': 'rollout', '#FFF3E0': 'rollout', '#F59E0B': 'rollout',
  // blues and purples — informational (ringing, inbound, outbound, PBX, API)
  '#1565C0': 'brand', '#E3F2FD': 'brand', '#7B1FA2': 'brand', '#6A1B9A': 'brand', '#F3E5F5': 'brand', '#00838F': 'brand',
  // greys — internal / rejected / ended / unknown / stale
  '#616161': 'neutral', '#F5F5F5': 'neutral', '#EEEEEE': 'neutral', '#6B7280': 'neutral',
  '#94A3B8': 'neutral', '#64748B': 'neutral',
  // MUI default palette mains used for priorities (critical, high, normal, low)
  '#F44336': 'urgent', '#FF9800': 'rollout', '#2196F3': 'brand', '#4CAF50': 'available',
};
const hexKind = (hex) => (typeof hex === 'string' ? HEX_STATUS[hex.toUpperCase()] : undefined);

/** A hand-picked status colour, recognised by its hex. Unknown colours pass through. */
export const hexFg = (theme, hex) => {
  const kind = hexKind(hex);
  return kind ? statusFg(theme, kind, hex) : hex;
};

/** `pairSx` for a `{ bg, color }` object whose meaning follows from its `color` hex. */
export const hexPairSx = (pair, extra) => pairSx(hexKind(pair.color) || 'neutral', pair, extra);

/**
 * `sx` callback for a Chip whose colours are derived from one status hex (a tint like
 * `${hex}22`, or a solid fill with white text). Plum/gold/presets get `fallbackSx` itself; under
 * Caro Editorial a known hex becomes its status pair — never transparent, icon in the pair's text
 * colour (T069, LD-12). Unknown hex values are left alone everywhere.
 */
export const statusChipSx = (hex, fallbackSx) => (theme) => {
  const kind = hexKind(hex);
  if (!theme?.caro?.statusChips || !kind) return fallbackSx;
  const s = theme.caro.status[kind];
  // eslint-disable-next-line no-unused-vars
  const { bgcolor, backgroundColor, background, color, ...rest } = fallbackSx;
  return {
    ...rest,
    backgroundColor: s.bg,
    color: s.fg,
    border: '1px solid',
    borderColor: s.border,
    '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: `${s.fg} !important` },
  };
};

/** A hand-picked status text/icon colour. Editorial: the status pair's `fg`. */
export const statusFg = (theme, kind, fallbackColor) =>
  theme?.caro?.statusChips ? (theme.caro.status[kind] || theme.caro.status.neutral).fg : fallbackColor;
